import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { functions } from "../@shared/firebase/config";
import { useAuthStore } from "../@shared/store/useAuthStore";
import {
  PHYSICAL_CARD_CLAIM_CODE_LENGTH,
  getPhysicalAssetIdValidationError,
  normalizePhysicalAssetIdInput,
} from "../@shared/utils/physicalCardClaim";

const emptySegments = Array.from(
  { length: PHYSICAL_CARD_CLAIM_CODE_LENGTH },
  () => "",
);

const statusCopy = {
  claimed: "Card claimed. Your digital character card has been added.",
  already_claimed: "This physical card has already been claimed.",
  not_found: "We couldn't find that asset ID. Check the code and try again.",
  invalid: "That asset ID is not in a valid format.",
  error: "Unable to claim this card right now. Please try again.",
};

const segmentInputSx = {
  width: 44,
  "& .MuiOutlinedInput-root": {
    color: "#fff",
    bgcolor: "rgba(255,255,255,0.04)",
    "& fieldset": { borderColor: "#4a3ab7" },
    "&:hover fieldset": { borderColor: "#6a58ff" },
    "&.Mui-focused fieldset": { borderColor: "#ffd24d" },
  },
  "& input": {
    textAlign: "center",
    fontWeight: 800,
    p: "11px 0",
  },
};

const ClaimPhysicalCardModal = ({ open, onClose }) => {
  const inputRefs = useRef([]);
  const loadUserCharacterCards = useAuthStore(
    (state) => state.loadUserCharacterCards,
  );
  const [segments, setSegments] = useState(emptySegments);
  const [hasHPrefix, setHasHPrefix] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const bodyValue = segments.join("");
  const parsed = useMemo(
    () => normalizePhysicalAssetIdInput(bodyValue, hasHPrefix),
    [bodyValue, hasHPrefix],
  );
  const validationError = useMemo(
    () => getPhysicalAssetIdValidationError(bodyValue, hasHPrefix),
    [bodyValue, hasHPrefix],
  );
  const canSubmit = parsed.isValid && !submitting;

  const reset = () => {
    setSegments(emptySegments);
    setHasHPrefix(false);
    setSubmitting(false);
    setFeedback(null);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose?.();
  };

  const applyPastedValue = (value, startIndex = 0) => {
    const pasted = normalizePhysicalAssetIdInput(value, false);
    if (pasted.hasHPrefix) {
      setHasHPrefix(true);
    }

    const chars = pasted.body
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, PHYSICAL_CARD_CLAIM_CODE_LENGTH - startIndex)
      .split("");

    if (!chars.length) return;

    setSegments((prev) => {
      const next = [...prev];
      chars.forEach((char, offset) => {
        next[startIndex + offset] = char;
      });
      return next;
    });

    const focusIndex = Math.min(
      PHYSICAL_CARD_CLAIM_CODE_LENGTH - 1,
      startIndex + chars.length,
    );
    inputRefs.current[focusIndex]?.focus();
  };

  const handleSegmentChange = (index) => (event) => {
    setFeedback(null);
    const value = event.target.value;

    if (value.length > 1) {
      applyPastedValue(value, index);
      return;
    }

    const char = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    setSegments((prev) => {
      const next = [...prev];
      next[index] = char.slice(-1);
      return next;
    });

    if (char && index < PHYSICAL_CARD_CLAIM_CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleSegmentPaste = (index) => (event) => {
    event.preventDefault();
    setFeedback(null);
    applyPastedValue(event.clipboardData.getData("text"), index);
  };

  const handleSegmentKeyDown = (index) => (event) => {
    if (event.key !== "Backspace" || segments[index]) return;
    if (index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    try {
      setSubmitting(true);
      setFeedback(null);
      const claimPhysicalCardByAssetId = functions.httpsCallable(
        "claimPhysicalCardByAssetId",
      );
      const result = await claimPhysicalCardByAssetId({
        assetIdInput: bodyValue,
        hasHPrefix,
      });
      const payload = result?.data || {};
      const status = payload.status || (payload.ok ? "claimed" : "error");

      setFeedback({
        severity: payload.ok || status === "already_claimed" ? "success" : "error",
        text: payload.message || statusCopy[status] || statusCopy.error,
      });

      if (payload.ok || status === "already_claimed") {
        loadUserCharacterCards({ force: true }).catch(() => {});
      }
    } catch (error) {
      setFeedback({
        severity: "error",
        text:
          error?.message ||
          "Unable to claim this card right now. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle
        sx={{
          bgcolor: "#12062a",
          color: "#ffd24d",
          fontWeight: 900,
        }}
      >
        Claim Physical Card
      </DialogTitle>
      <DialogContent
        sx={{
          bgcolor: "#05010f",
          pt: 2,
        }}
      >
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ color: "#9aa0ff" }}>
            Enter the asset ID printed on the card.
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={hasHPrefix}
                onChange={(event) => {
                  setFeedback(null);
                  setHasHPrefix(event.target.checked);
                }}
                sx={{
                  color: "#9aa0ff",
                  "&.Mui-checked": { color: "#ffd24d" },
                }}
              />
            }
            label="My asset ID starts with H-"
            sx={{ color: "#e2d5ff", m: 0 }}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 44px)",
              gap: 1,
              justifyContent: "center",
              "@media (min-width: 420px)": {
                gridTemplateColumns: "repeat(8, 44px)",
              },
            }}
          >
            {segments.map((segment, index) => (
              <TextField
                key={index}
                inputRef={(node) => {
                  inputRefs.current[index] = node;
                }}
                value={segment}
                onChange={handleSegmentChange(index)}
                onPaste={handleSegmentPaste(index)}
                onKeyDown={handleSegmentKeyDown(index)}
                inputProps={{
                  maxLength: 1,
                  "aria-label": `Asset ID character ${index + 1}`,
                }}
                sx={segmentInputSx}
              />
            ))}
          </Box>

          <Box
            sx={{
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 2,
              p: 1.25,
              bgcolor: "rgba(111,82,237,0.08)",
            }}
          >
            <Typography variant="caption" sx={{ color: "#9aa0ff" }}>
              Parsed asset ID
            </Typography>
            <Typography sx={{ color: "#fff", fontWeight: 800 }}>
              {parsed.assetId || (hasHPrefix ? "H-" : "")}
            </Typography>
          </Box>

          {bodyValue && validationError ? (
            <Typography variant="body2" sx={{ color: "#ffb3c7" }}>
              {validationError}
            </Typography>
          ) : null}

          {feedback ? (
            <Typography
              variant="body2"
              sx={{
                color: feedback.severity === "success" ? "#8ff0bf" : "#ffb3c7",
              }}
            >
              {feedback.text}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ bgcolor: "#05010f", borderTop: "1px solid #120a3a" }}>
        <Button onClick={handleClose} disabled={submitting} sx={{ color: "#e2d5ff" }}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{
            fontWeight: 700,
            bgcolor: "#6f52ed",
            "&:hover": { bgcolor: "#5a3ed3" },
          }}
        >
          {submitting ? "Claiming..." : "Claim Card"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClaimPhysicalCardModal;
